import { useCallback, useEffect, useState } from "react";
import { computeStatistics } from "../../Statistics/utils";
import { detectNumericScale } from "../utils/chartHelpers";

const normalizeValue = (value) => String(value ?? "").trim();

const hasMappingEntries = (mapping) =>
  Boolean(mapping && typeof mapping === "object" && Object.keys(mapping).length > 0);

const lookupMappingEntry = (mapping, rawKey) => {
  if (!mapping || !rawKey) return null;
  if (Object.prototype.hasOwnProperty.call(mapping, rawKey)) return mapping[rawKey];

  const keyByTrimMatch = Object.entries(mapping).find(
    ([mappingKey]) => normalizeValue(mappingKey) === rawKey
  );
  if (keyByTrimMatch) return keyByTrimMatch[1];

  const numericKey = Number(rawKey);
  if (Number.isFinite(numericKey)) {
    const fallbackKey = String(numericKey);
    if (Object.prototype.hasOwnProperty.call(mapping, fallbackKey)) return mapping[fallbackKey];

    const keyByNumericMatch = Object.entries(mapping).find(([mappingKey]) => {
      const normalizedKey = normalizeValue(mappingKey);
      if (!normalizedKey) return false;
      const normalizedNumericKey = Number(normalizedKey);
      return Number.isFinite(normalizedNumericKey) && normalizedNumericKey === numericKey;
    });
    if (keyByNumericMatch) return keyByNumericMatch[1];
  }

  return null;
};

export const useChartStatistics = ({
  sqlData,
  selectedIndependent,
  selectedDependent,
  useWeightedData,
  weightColumn,
  dependentAnswerMapping,
  questionsIndex,
  indepIsGrouped,
  indepGroupMembers,
  indepGroupDisplay,
  applyActiveFilters,
  shouldCompute,
}) => {
  const [showStats, setShowStats] = useState(false);
  const [nationalStats, setNationalStats] = useState(null);
  const [indepStats, setIndepStats] = useState([]);
  const [selectedIndepBucket, setSelectedIndepBucket] = useState("All");
  const [sortBy, setSortBy] = useState("label_asc");

  const getRowWeight = useCallback(
    (row) => {
      if (!useWeightedData || !weightColumn) return 1;
      const parsed = Number(row?.[weightColumn]);
      if (!Number.isFinite(parsed)) return 1;
      return Math.max(0, parsed);
    },
    [useWeightedData, weightColumn]
  );

  const recomputeStats = useCallback(() => {
    if (!sqlData.length || !selectedDependent) {
      setNationalStats(null);
      setIndepStats([]);
      return;
    }

    const independentAnswerMap = selectedIndependent
      ? questionsIndex[selectedIndependent]?.answersMapping || {}
      : {};
    const enforceDependentMapping = hasMappingEntries(dependentAnswerMapping);
    const enforceIndependentMapping =
      selectedIndependent && hasMappingEntries(independentAnswerMap);

    let filtered = applyActiveFilters(sqlData);
    filtered = filtered.filter((row) => {
      const dependentRaw = normalizeValue(row[selectedDependent]);
      if (!dependentRaw) return false;
      if (enforceDependentMapping && !lookupMappingEntry(dependentAnswerMapping, dependentRaw)) {
        return false;
      }
      if (!selectedIndependent || !enforceIndependentMapping) return true;
      const independentRaw = normalizeValue(row[selectedIndependent]);
      return Boolean(independentRaw && lookupMappingEntry(independentAnswerMap, independentRaw));
    });

    setNationalStats(
      computeStatistics(filtered, selectedDependent, {
        weightAccessor: getRowWeight,
      })
    );

    if (!selectedIndependent) {
      setIndepStats([]);
      if (selectedIndepBucket !== "All") {
        setSelectedIndepBucket("All");
      }
      return;
    }

    let buckets = [];
    if (indepIsGrouped && Object.keys(indepGroupMembers).length) {
      buckets = Object.keys(indepGroupMembers).map((groupId) => ({
        id: groupId,
        label: indepGroupDisplay[groupId] || groupId,
        members: new Set((indepGroupMembers[groupId] || []).map((value) => normalizeValue(value))),
      }));
    } else if (Object.keys(independentAnswerMap).length) {
      buckets = Object.entries(independentAnswerMap).map(([raw, value]) => ({
        id: String(raw),
        label: value?.Display ?? String(raw),
        members: new Set([normalizeValue(raw)]),
      }));
    }

    const perBucket = buckets
      .map((bucket) => {
        const rows = filtered.filter((row) =>
          bucket.members.has(normalizeValue(row[selectedIndependent]))
        );
        const stats = computeStatistics(rows, selectedDependent, {
          weightAccessor: getRowWeight,
        });
        return { label: bucket.label, ...stats };
      })
      .filter((bucket) => bucket.sampleSize > 0)
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));

    setIndepStats(perBucket);
    if (
      selectedIndepBucket !== "All" &&
      !perBucket.some((bucket) => bucket.label === selectedIndepBucket)
    ) {
      setSelectedIndepBucket("All");
    }
  }, [
    sqlData,
    selectedDependent,
    selectedIndependent,
    applyActiveFilters,
    dependentAnswerMapping,
    questionsIndex,
    indepIsGrouped,
    indepGroupMembers,
    indepGroupDisplay,
    getRowWeight,
    selectedIndepBucket,
  ]);

  useEffect(() => {
    const shouldRecompute = typeof shouldCompute === "boolean" ? shouldCompute : showStats;
    if (shouldRecompute) {
      recomputeStats();
    }
  }, [showStats, recomputeStats, shouldCompute]);

  return {
    showStats,
    setShowStats,
    nationalStats,
    indepStats,
    selectedIndepBucket,
    setSelectedIndepBucket,
    sortBy,
    setSortBy,
    detectNumericScale,
  };
};
